import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Building2, MapPin, Phone, Mail, FileText, Loader2, CreditCard } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Deposit types
const DEPOSIT_TYPES = [
  { id: "carta_credito", label: "Carta di Credito" },
  { id: "bancomat", label: "Bancomat" },
  { id: "assegno", label: "Assegno" },
  { id: "contanti", label: "Contanti" },
];

export default function CompanyProfile() {
  const { user, token, updateUser } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    address: "",
    city: "",
    province: "",
    cap: "",
    phone: "",
    email: "",
    vat_number: "",
    accepted_deposits: []
  });

  useEffect(() => {
    if (user?.company_id) {
      fetchCompany();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchCompany = async () => {
    try {
      const res = await axios.get(`${API}/companies/${user.company_id}`);
      setCompany(res.data);
      setFormData({
        name: res.data.name || "",
        description: res.data.description || "",
        address: res.data.address || "",
        city: res.data.city || "",
        province: res.data.province || "",
        cap: res.data.cap || "",
        phone: res.data.phone || "",
        email: res.data.email || "",
        vat_number: res.data.vat_number || "",
        accepted_deposits: res.data.accepted_deposits || []
      });
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDepositChange = (depositId) => {
    setFormData(prev => {
      const deposits = prev.accepted_deposits || [];
      if (deposits.includes(depositId)) {
        return { ...prev, accepted_deposits: deposits.filter(d => d !== depositId) };
      } else {
        return { ...prev, accepted_deposits: [...deposits, depositId] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (company) {
        // Update existing company
        await axios.put(`${API}/companies/${company.company_id}`, formData, { headers, withCredentials: true });
        toast.success("Azienda aggiornata");
        fetchCompany();
      } else {
        // Create new company
        const res = await axios.post(`${API}/companies`, formData, { headers, withCredentials: true });
        toast.success("Azienda registrata");
        updateUser({ company_id: res.data.company_id, role: "company" });
        setCompany(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Profilo Azienda">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Profilo Azienda">
      <div className="max-w-2xl" data-testid="company-profile">
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {company ? "Modifica Azienda" : "Registra Azienda"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="input-label">Nome Azienda *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10"
                    placeholder="es. Noleggio Rossi Srl"
                    required
                    data-testid="company-name"
                  />
                </div>
              </div>

              <div>
                <Label className="input-label">Partita IVA *</Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={formData.vat_number}
                    onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                    className="pl-10"
                    placeholder="IT12345678901"
                    required
                    data-testid="company-vat"
                  />
                </div>
              </div>

              <div>
                <Label className="input-label">Descrizione</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrivi la tua azienda..."
                  rows={3}
                />
              </div>

              <div>
                <Label className="input-label">Indirizzo *</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="pl-10"
                    placeholder="Via Roma 1"
                    required
                    data-testid="company-address"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="input-label">Città *</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Milano"
                    required
                    data-testid="company-city"
                  />
                </div>
                <div>
                  <Label className="input-label">Provincia *</Label>
                  <Input
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value.toUpperCase() })}
                    placeholder="MI"
                    maxLength={2}
                    required
                    data-testid="company-province"
                  />
                </div>
                <div>
                  <Label className="input-label">CAP *</Label>
                  <Input
                    value={formData.cap}
                    onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                    placeholder="20100"
                    required
                    data-testid="company-cap"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="input-label">Telefono *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="pl-10"
                      placeholder="+39 02 1234567"
                      required
                      data-testid="company-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label className="input-label">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10"
                      placeholder="info@azienda.it"
                      required
                      data-testid="company-email"
                    />
                  </div>
                </div>
              </div>

              {/* Accepted Deposits */}
              <div>
                <Label className="input-label flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Metodi di Caparra Accettati
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Seleziona i metodi di pagamento che accetti per la caparra
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {DEPOSIT_TYPES.map((deposit) => (
                    <label 
                      key={deposit.id} 
                      htmlFor={`deposit-${deposit.id}`}
                      className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-muted/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        id={`deposit-${deposit.id}`}
                        checked={(formData.accepted_deposits || []).includes(deposit.id)}
                        onChange={() => handleDepositChange(deposit.id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm flex-1">
                        {deposit.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <Button type="submit" className="btn-primary" disabled={submitting} data-testid="save-company">
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  company ? "Salva Modifiche" : "Registra Azienda"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
